import React from "react";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Vital } from "@/lib/dashboard/vitalsData";
import {
  calculateNEWS2,
  extractSystolicBP,
  NEWS2Parameters,
} from "@/lib/dashboard/news2Calculator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TooltipPortal,
} from "@/components/ui/tooltip";

interface NEWS2ScorePanelCompactProps {
  vitals: Vital[];
  isDarkMode: boolean;
}

export function NEWS2ScorePanelCompact({
  vitals,
  isDarkMode,
}: NEWS2ScorePanelCompactProps) {
  // Extract NEWS2 parameters from vitals
  const params: NEWS2Parameters = {};
  let rawBloodPressure: string | undefined;

  vitals.forEach((vital) => {
    switch (vital.type) {
      case "respiratory-rate":
        params.respirationRate = parseFloat(vital.value);
        break;
      case "oxygen-saturation":
        params.oxygenSaturation = parseFloat(vital.value);
        break;
      case "blood-pressure":
        rawBloodPressure = vital.value;
        params.systolicBloodPressure = extractSystolicBP(vital.value);
        break;
      case "heart-rate":
        params.pulseRate = parseFloat(vital.value);
        break;
      case "temperature":
        params.temperature = parseFloat(vital.value);
        break;
      case "consciousness-level":
        params.consciousness = vital.value as
          | "Alert"
          | "Voice"
          | "Pain"
          | "Unresponsive";
        break;
      case "supplemental-oxygen":
        params.supplementalOxygen =
          vital.value === "Yes" || vital.value === "true";
        break;
    }
  });

  // Define required parameters for NEWS2
  const requiredParams = [
    { key: "respirationRate", label: "RR", fullLabel: "Respiratory Rate" },
    { key: "oxygenSaturation", label: "SpO₂", fullLabel: "Oxygen Saturation" },
    { key: "systolicBloodPressure", label: "BP", fullLabel: "Blood Pressure" },
    { key: "pulseRate", label: "HR", fullLabel: "Pulse Rate" },
    { key: "consciousness", label: "AVPU", fullLabel: "Consciousness" },
    { key: "temperature", label: "Temp", fullLabel: "Temperature" },
  ];

  const missingParams = requiredParams.filter(
    (param) => params[param.key as keyof NEWS2Parameters] === undefined,
  );

  const isComplete = missingParams.length === 0;

  const news2Score = calculateNEWS2(params);

  // Get color scheme based on risk level
  const getRiskColors = () => {
    switch (news2Score.riskLevel) {
      case "none":
        return {
          bg: isDarkMode ? "bg-gray-700/50" : "bg-gray-100",
          border: isDarkMode ? "border-gray-600" : "border-gray-300",
          text: isDarkMode ? "text-gray-300" : "text-gray-700",
          badge: "bg-gray-500",
          icon: CheckCircle,
        };
      case "low":
        return {
          bg: isDarkMode ? "bg-blue-900/30" : "bg-blue-50",
          border: isDarkMode ? "border-blue-700/50" : "border-blue-200",
          text: isDarkMode ? "text-blue-300" : "text-blue-700",
          badge: "bg-blue-500",
          icon: Info,
        };
      case "low-medium":
        return {
          bg: isDarkMode ? "bg-yellow-900/30" : "bg-yellow-50",
          border: isDarkMode ? "border-yellow-700/50" : "border-yellow-200",
          text: isDarkMode ? "text-yellow-300" : "text-yellow-700",
          badge: "bg-yellow-500",
          icon: AlertCircle,
        };
      case "medium":
        return {
          bg: isDarkMode ? "bg-orange-900/30" : "bg-orange-50",
          border: isDarkMode ? "border-orange-700/50" : "border-orange-200",
          text: isDarkMode ? "text-orange-300" : "text-orange-700",
          badge: "bg-orange-500",
          icon: AlertTriangle,
        };
      case "high":
        return {
          bg: isDarkMode ? "bg-red-900/30" : "bg-red-50",
          border: isDarkMode ? "border-red-700/50" : "border-red-200",
          text: isDarkMode ? "text-red-300" : "text-red-700",
          badge: "bg-red-500",
          icon: AlertTriangle,
        };
      default:
        return {
          bg: isDarkMode ? "bg-gray-700/50" : "bg-gray-100",
          border: isDarkMode ? "border-gray-600" : "border-gray-300",
          text: isDarkMode ? "text-gray-300" : "text-gray-700",
          badge: "bg-gray-500",
          icon: Activity,
        };
    }
  };

  const colors = getRiskColors();
  const Icon = colors.icon;

  // Get risk level display text
  const getRiskLevelText = () => {
    switch (news2Score.riskLevel) {
      case "none":
        return "No Risk";
      case "low":
        return "Low";
      case "low-medium":
        return "Low-Med";
      case "medium":
        return "Medium";
      case "high":
        return "High";
      default:
        return "Unknown";
    }
  };

  // Render incomplete state - compact version
  if (!isComplete) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                isDarkMode
                  ? "bg-blue-900/20 border-blue-700/50"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <Info
                className={`w-3.5 h-3.5 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
              />
              <span
                className={`text-xs font-medium ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
              >
                NEWS2:
              </span>
              <Badge
                className={`${
                  isDarkMode ? "bg-blue-700" : "bg-blue-500"
                } text-white text-xs h-5 px-2`}
              >
                {6 - missingParams.length}/6
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-xs" side="bottom" align="end">
              <div className="space-y-2">
                <p className="font-semibold text-sm">NEWS2 Score Incomplete</p>
                <p className="text-xs">
                  Missing {missingParams.length} of 6 required vitals:
                </p>
                <div className="flex flex-wrap gap-1">
                  {missingParams.map((param) => (
                    <Badge
                      key={param.key}
                      variant="outline"
                      className="text-xs"
                    >
                      {param.fullLabel}
                    </Badge>
                  ))}
                </div>
              </div>
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Render complete state - compact version
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${colors.bg} ${colors.border}`}
          >
            <Icon className={`w-3.5 h-3.5 ${colors.text}`} />
            <span
              className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              NEWS2:
            </span>
            <span className={`text-sm font-bold ${colors.text}`}>
              {news2Score.total}
            </span>
            <Badge className={`${colors.badge} text-white text-xs h-5 px-2`}>
              {getRiskLevelText()}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent className="max-w-sm" side="bottom" align="end">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-sm">
                  NEWS2 Score: {news2Score.total}
                </p>
                <Badge className={`${colors.badge} text-white text-xs`}>
                  {getRiskLevelText()} Risk
                </Badge>
              </div>
              <p className="text-xs">{news2Score.clinicalResponse}</p>
              <div className="pt-2 border-t space-y-1">
                <p className="text-xs font-semibold">Parameters:</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {params.respirationRate !== undefined && (
                    <div>
                      RR: {params.respirationRate} ({news2Score.respirationRate}
                      )
                    </div>
                  )}
                  {params.oxygenSaturation !== undefined && (
                    <div>
                      SpO₂: {params.oxygenSaturation}% (
                      {news2Score.oxygenSaturation})
                      <span className="opacity-60 ml-1">
                        {params.supplementalOxygen ? "Scale 2" : "Scale 1"}
                      </span>
                    </div>
                  )}
                  {params.systolicBloodPressure !== undefined && (
                    <div>
                      BP: {rawBloodPressure ?? params.systolicBloodPressure} (
                      {news2Score.systolicBloodPressure})
                    </div>
                  )}
                  {params.pulseRate !== undefined && (
                    <div>
                      HR: {params.pulseRate} ({news2Score.pulseRate})
                    </div>
                  )}
                  {params.temperature !== undefined && (
                    <div>
                      Temp: {params.temperature}°C ({news2Score.temperature})
                    </div>
                  )}
                  {params.consciousness !== undefined && (
                    <div>
                      AVPU: {params.consciousness} ({news2Score.consciousness})
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}
